/**
 * Button component
 *
 * Built on top of the Radix UI `Slot` primitive so it supports the `asChild`
 * composition pattern.  Variants and sizes are defined with Class Variance
 * Authority (CVA) to keep the class lists typed and tree-shakable.
 *
 * @example Basic usage
 *   <Button variant="primary" size="md">Save changes</Button>
 *
 * @example Loading state
 *   <Button variant="primary" isLoading>Saving…</Button>
 *
 * @example Icon button
 *   <Button variant="ghost" size="icon" aria-label="Close">
 *     <X className="size-4" />
 *   </Button>
 *
 * @example asChild — render as a Next.js <Link>
 *   <Button asChild variant="link">
 *     <Link href="/dashboard">Go to dashboard</Link>
 *   </Button>
 */

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "../utils/cn";

// ---------------------------------------------------------------------------
// CVA config
// ---------------------------------------------------------------------------

const buttonVariants = cva(
  // Base styles applied to every variant
  [
    "inline-flex items-center justify-center gap-2",
    "rounded-md font-medium",
    "whitespace-nowrap select-none",
    "ring-offset-background transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:pointer-events-none disabled:opacity-50",
  ],
  {
    variants: {
      variant: {
        /** Solid primary-brand background. Use for the single most important CTA per view. */
        primary:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90 active:bg-primary/80",
        /** Outlined style — secondary or cancel action. */
        secondary:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground active:bg-accent/80",
        /** Solid destructive background. Use for irreversible actions (delete, revoke). */
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 active:bg-destructive/80",
        /** No background or border — low-emphasis action. */
        ghost:
          "hover:bg-accent hover:text-accent-foreground active:bg-accent/80",
        /** Text-only with underline on hover. Use inside prose or inline contexts. */
        link: "text-primary underline-offset-4 hover:underline",
        /** Outlined with transparent background. Alternative to secondary. */
        outline:
          "border border-input bg-transparent shadow-sm hover:bg-accent hover:text-accent-foreground",
        /** Primary-colored outline on transparent background. Use as the low-emphasis sibling to `gradient-primary`. */
        "outline-primary":
          "border border-primary bg-transparent text-primary shadow-sm " +
          "hover:bg-primary/10 active:bg-primary/15",
        /** Epic-purple gradient fill. Use as the single highest-emphasis CTA per view. */
        "gradient-primary":
          "text-primary-foreground shadow " +
          "[background-image:var(--gradient-epic)] " +
          "hover:brightness-110 active:brightness-95 transition-[filter]",
      },
      size: {
        /** @deprecated Prefer sm/md/lg for new ENG-1403 layouts */
        xs: "h-7 px-2.5 text-xs",
        /** Design-system size (ENG-1403) */
        sm: "h-8 px-3 text-sm",
        /** Design-system size (ENG-1403) */
        md: "h-9 px-4 py-2 text-sm",
        /** Design-system size (ENG-1403) */
        lg: "h-10 px-6 text-base",
        /** @deprecated Prefer sm/md/lg for new ENG-1403 layouts */
        xl: "h-12 px-8 text-base",
        /** Square icon button — medium size. */
        icon: "size-9",
        /** Square icon button — small size. */
        "icon-sm": "size-7",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * When true, renders the button's children as the child element (via Radix
   * Slot).  The button itself is not rendered — all props are merged onto the
   * child.  Useful for wrapping <Link> or other interactive elements while
   * retaining button styling.
   */
  asChild?: boolean;
  /** Shows a spinner and disables the button when true. */
  isLoading?: boolean;
  /** Icon rendered to the left of the label. Hidden while `isLoading`. */
  leftIcon?: React.ReactNode;
  /** Icon rendered to the right of the label. Hidden while `isLoading`. */
  rightIcon?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading = false,
      leftIcon,
      rightIcon,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled || isLoading}
        aria-disabled={disabled || isLoading}
        aria-busy={isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            {children}
          </>
        ) : (
          <>
            {leftIcon && (
              <span className="shrink-0" aria-hidden="true">
                {leftIcon}
              </span>
            )}
            {children}
            {rightIcon && (
              <span className="shrink-0" aria-hidden="true">
                {rightIcon}
              </span>
            )}
          </>
        )}
      </Comp>
    );
  },
);

Button.displayName = "Button";

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { Button, buttonVariants };
